/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListToolsetToolSchemaStaticValuesRequest,
  ListToolsetToolSchemaStaticValuesSecurity,
} from "../models/operations/listtoolsettoolschemastaticvalues.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListToolSchemaStaticValuesQuery,
  ListToolSchemaStaticValuesQueryData,
  prefetchListToolSchemaStaticValues,
  queryKeyListToolSchemaStaticValues,
} from "./listToolSchemaStaticValues.core.js";
export {
  buildListToolSchemaStaticValuesQuery,
  type ListToolSchemaStaticValuesQueryData,
  prefetchListToolSchemaStaticValues,
  queryKeyListToolSchemaStaticValues,
};

export type ListToolSchemaStaticValuesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listToolSchemaStaticValues toolsets
 *
 * @remarks
 * List every const, default, enum, example, and examples value in each tool input schema. The result is deliberately mechanical: clients decide how to present or interpret the values. This supports reviewing the exact schema contents before changing how a toolset is shared.
 */
export function useListToolSchemaStaticValues(
  request: ListToolsetToolSchemaStaticValuesRequest,
  security?: ListToolsetToolSchemaStaticValuesSecurity | undefined,
  options?: QueryHookOptions<
    ListToolSchemaStaticValuesQueryData,
    ListToolSchemaStaticValuesQueryError
  >,
): UseQueryResult<
  ListToolSchemaStaticValuesQueryData,
  ListToolSchemaStaticValuesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildListToolSchemaStaticValuesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listToolSchemaStaticValues toolsets
 *
 * @remarks
 * List every const, default, enum, example, and examples value in each tool input schema. The result is deliberately mechanical: clients decide how to present or interpret the values. This supports reviewing the exact schema contents before changing how a toolset is shared.
 */
export function useListToolSchemaStaticValuesSuspense(
  request: ListToolsetToolSchemaStaticValuesRequest,
  security?: ListToolsetToolSchemaStaticValuesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListToolSchemaStaticValuesQueryData,
    ListToolSchemaStaticValuesQueryError
  >,
): UseSuspenseQueryResult<
  ListToolSchemaStaticValuesQueryData,
  ListToolSchemaStaticValuesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListToolSchemaStaticValuesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListToolSchemaStaticValuesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      slug: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListToolSchemaStaticValuesQueryData,
): ListToolSchemaStaticValuesQueryData | undefined {
  const key = queryKeyListToolSchemaStaticValues(...queryKeyBase);

  return client.setQueryData<ListToolSchemaStaticValuesQueryData>(key, data);
}

export function invalidateListToolSchemaStaticValues(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      slug: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "toolsets",
      "listToolSchemaStaticValues",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllListToolSchemaStaticValues(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "toolsets", "listToolSchemaStaticValues"],
  });
}
